/**
 * Restroom Survey Configuration Page - HOTELS ON TAP
 * 
 * Admin-controlled restroom survey question management.
 * - Edit question text (EN/FR)
 * - Enable/disable questions
 * - Reorder questions (up/down)
 * - Rating type only (1-5 stars)
 * 
 * Constraints: Min 0, Max 5 questions. All must be rating type.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hotelsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import LanguageSelector from '../../components/LanguageSelector';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  Save,
  GripVertical,
  Droplets,
  Star,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function RestroomSurveyConfiguration() {
  const { t, i18n } = useTranslation();
  const { id: hotelId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');
  
  const [hotel, setHotel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  
  // Form state
  const [newQuestion, setNewQuestion] = useState({
    question_text_en: '',
    question_text_fr: '',
  });

  useEffect(() => {
    fetchData();
  }, [hotelId]);

  const fetchData = async () => {
    try {
      // Fetch hotel info
      const hotelRes = await hotelsAPI.get(hotelId);
      setHotel(hotelRes.data);
      
      // Fetch restroom survey config
      const configRes = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${hotelId}/survey-definitions/restroom`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (configRes.ok) {
        const configData = await configRes.json();
        // Transform questions to have enabled and order properties
        const qs = (configData.questions || []).map((q, idx) => ({
          ...q,
          id: q.id || `q-${idx}`,
          question_text_en: typeof q.text === 'object' ? q.text.en : q.text,
          question_text_fr: typeof q.text === 'object' ? q.text.fr : q.text,
          response_type: 'rating', // Always rating for restroom
          enabled: q.enabled !== false,
          order: q.order || idx + 1
        }));
        setQuestions(qs);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load survey configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Transform questions back to API format
      const apiQuestions = questions
        .filter(q => q.enabled)
        .sort((a, b) => a.order - b.order)
        .map((q, idx) => ({
          id: q.id,
          text: { en: q.question_text_en, fr: q.question_text_fr },
          response_type: 'rating',
          order: idx + 1,
          enabled: true
        }));
      
      const res = await fetch(`${API_BASE}/api/hotels-standalone/organizations/${hotelId}/survey-definitions/restroom`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ questions: apiQuestions })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(i18n.language === 'fr' ? 'Configuration sauvegardée' : 'Configuration saved');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = () => {
    if (questions.length >= 5) {
      toast.error(i18n.language === 'fr' ? 'Maximum 5 questions pour les sondages toilettes' : 'Maximum 5 questions for restroom surveys');
      return;
    }
    
    if (!newQuestion.question_text_en.trim() || !newQuestion.question_text_fr.trim()) {
      toast.error(i18n.language === 'fr' ? 'Veuillez remplir les deux langues' : 'Please fill in both languages');
      return;
    }
    
    const newQ = {
      id: `new-${Date.now()}`,
      question_text_en: newQuestion.question_text_en,
      question_text_fr: newQuestion.question_text_fr,
      response_type: 'rating',
      enabled: true,
      order: questions.length + 1
    };
    
    setQuestions([...questions, newQ]);
    setShowAddModal(false);
    setNewQuestion({ question_text_en: '', question_text_fr: '' });
    setHasChanges(true);
    toast.success(i18n.language === 'fr' ? 'Question ajoutée' : 'Question added');
  };

  const handleUpdateQuestion = () => {
    if (!editingQuestion) return;
    
    setQuestions(questions.map(q => 
      q.id === editingQuestion.id ? editingQuestion : q
    ));
    setShowEditModal(false);
    setEditingQuestion(null);
    setHasChanges(true);
    toast.success(i18n.language === 'fr' ? 'Question mise à jour' : 'Question updated');
  };

  const handleDeleteQuestion = () => {
    if (!deletingQuestion) return;
    
    setQuestions(questions.filter(q => q.id !== deletingQuestion.id));
    setShowDeleteDialog(false);
    setDeletingQuestion(null);
    setHasChanges(true);
    toast.success(i18n.language === 'fr' ? 'Question supprimée' : 'Question deleted');
  };

  const handleToggleEnabled = (questionId, enabled) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, enabled } : q
    ));
    setHasChanges(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newQuestions = [...questions];
    [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    newQuestions.forEach((q, i) => q.order = i + 1);
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const handleMoveDown = (index) => {
    if (index === questions.length - 1) return;
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    newQuestions.forEach((q, i) => q.order = i + 1);
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const enabledQuestions = questions.filter(q => q.enabled);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)]">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/standalone/cards?type=restroom')}
              data-testid="back-btn"
              className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{i18n.language === 'fr' ? 'Tags Toilettes' : 'Restroom Tags'}</span>
            </button>
            <div className="h-6 w-px bg-[hsl(217,19%,27%)]" />
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <span className="text-lg font-semibold text-white">
                  {i18n.language === 'fr' ? 'Configuration Sondage Toilettes' : 'Restroom Survey Configuration'}
                </span>
                <p className="text-xs text-[hsl(215,20%,65%)]">{hotel?.name}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <LanguageSelector variant="compact" />
            
            <Button
              variant="outline"
              data-testid="preview-btn"
              onClick={() => setShowPreview(true)}
              className="border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,17%)]"
            >
              <Eye className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Aperçu' : 'Preview'}
            </Button>
            
            {hasChanges && (
              <Button
                data-testid="save-all-btn"
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {i18n.language === 'fr' ? 'Sauvegarder' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Info Banner */}
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
          <div className="flex items-start space-x-3">
            <Droplets className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium mb-1">
                {i18n.language === 'fr' ? 'Sondage Toilettes' : 'Restroom Survey'}
              </p>
              <p className="text-blue-300/80">
                {i18n.language === 'fr' 
                  ? 'Les sondages toilettes peuvent avoir de 0 à 5 questions. Toutes les questions utilisent des évaluations 1-5 étoiles.'
                  : 'Restroom surveys can have 0 to 5 questions. All questions use 1-5 star ratings.'}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center space-x-4 text-sm text-[hsl(215,20%,65%)]">
          <span>{questions.length} {i18n.language === 'fr' ? 'questions' : 'questions'}</span>
          <span>•</span>
          <span>{enabledQuestions.length} {i18n.language === 'fr' ? 'activées' : 'enabled'}</span>
          <span>•</span>
          <span className="text-blue-400">{i18n.language === 'fr' ? 'Max 5 questions' : 'Max 5 questions'}</span>
        </div>

        {/* Questions List */}
        <div className="space-y-3">
          {questions.length === 0 ? (
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] border-dashed">
              <CardContent className="py-8 text-center">
                <Droplets className="w-10 h-10 text-blue-400/50 mx-auto mb-3" />
                <p className="text-white font-medium mb-2">
                  {i18n.language === 'fr' ? 'Aucune question configurée' : 'No questions configured'}
                </p>
                <p className="text-[hsl(215,20%,65%)] text-sm mb-4">
                  {i18n.language === 'fr' 
                    ? 'Les clients pourront seulement laisser des commentaires texte.'
                    : 'Guests will only be able to leave text feedback.'}
                </p>
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Ajouter une question' : 'Add Question'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            questions.sort((a, b) => a.order - b.order).map((question, index) => (
              <Card
                key={question.id}
                data-testid={`question-card-${index}`}
                className={`bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] ${
                  !question.enabled ? 'opacity-60' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    {/* Drag Handle & Order */}
                    <div className="flex flex-col items-center space-y-1 pt-1">
                      <GripVertical className="w-4 h-4 text-[hsl(215,20%,45%)]" />
                      <span className="text-xs text-[hsl(215,20%,65%)] font-medium">Q{index + 1}</span>
                    </div>
                    
                    {/* Question Content */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="text-white font-medium">
                            {i18n.language === 'fr' ? question.question_text_fr : question.question_text_en}
                          </p>
                          <div className="flex items-center space-x-3 text-xs">
                            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                              {i18n.language === 'fr' ? 'Évaluation 1-5' : 'Rating 1-5'}
                            </span>
                            {question.enabled ? (
                              <span className="text-green-400">{i18n.language === 'fr' ? 'Activée' : 'Enabled'}</span>
                            ) : (
                              <span className="text-red-400">{i18n.language === 'fr' ? 'Désactivée' : 'Disabled'}</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center space-x-2">
                          <Switch
                            data-testid={`toggle-q-${index}`}
                            checked={question.enabled}
                            onCheckedChange={(checked) => handleToggleEnabled(question.id, checked)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="text-[hsl(215,20%,65%)] hover:text-white"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === questions.length - 1}
                            className="text-[hsl(215,20%,65%)] hover:text-white"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-q-${index}`}
                            onClick={() => {
                              setEditingQuestion({ ...question });
                              setShowEditModal(true);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-q-${index}`}
                            onClick={() => {
                              setDeletingQuestion(question);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Show both languages */}
                      <div className="text-xs text-[hsl(215,20%,45%)] pt-2 border-t border-[hsl(217,19%,27%)]">
                        <p><span className="text-[hsl(215,20%,65%)]">EN:</span> {question.question_text_en}</p>
                        <p><span className="text-[hsl(215,20%,65%)]">FR:</span> {question.question_text_fr}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Add Question Button */}
        {questions.length < 5 && questions.length > 0 && (
          <Button
            data-testid="add-question-btn"
            onClick={() => setShowAddModal(true)}
            variant="outline"
            className="w-full border-dashed border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,17%)]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {i18n.language === 'fr' ? 'Ajouter une question' : 'Add Question'}
          </Button>
        )}

        {/* Max reached notice */}
        {questions.length >= 5 && (
          <p className="text-center text-sm text-amber-400">
            {i18n.language === 'fr' ? 'Maximum de 5 questions atteint' : 'Maximum of 5 questions reached'}
          </p>
        )}
      </main>

      {/* Add Question Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle>{i18n.language === 'fr' ? 'Ajouter une question' : 'Add Question'}</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              {i18n.language === 'fr' 
                ? 'Les questions de sondage toilettes utilisent des évaluations 1-5 étoiles.'
                : 'Restroom survey questions use 1-5 star ratings.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{i18n.language === 'fr' ? 'Question (Anglais)' : 'Question (English)'}</Label>
              <Textarea
                data-testid="new-q-en"
                value={newQuestion.question_text_en}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_text_en: e.target.value })}
                placeholder="Enter question in English..."
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{i18n.language === 'fr' ? 'Question (Français)' : 'Question (French)'}</Label>
              <Textarea
                data-testid="new-q-fr"
                value={newQuestion.question_text_fr}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_text_fr: e.target.value })}
                placeholder="Entrez la question en français..."
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-center space-x-2 text-amber-400 text-sm">
                <Star className="w-4 h-4" />
                <span>{i18n.language === 'fr' ? 'Type de réponse: Évaluation 1-5 étoiles' : 'Response type: 1-5 star rating'}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-[hsl(215,20%,65%)]">
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              data-testid="confirm-add-btn"
              onClick={handleAddQuestion}
              disabled={!newQuestion.question_text_en || !newQuestion.question_text_fr}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {i18n.language === 'fr' ? 'Ajouter' : 'Add Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle>{i18n.language === 'fr' ? 'Modifier la question' : 'Edit Question'}</DialogTitle>
          </DialogHeader>
          
          {editingQuestion && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'Question (Anglais)' : 'Question (English)'}</Label>
                <Textarea
                  data-testid="edit-q-en"
                  value={editingQuestion.question_text_en}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text_en: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'Question (Français)' : 'Question (French)'}</Label>
                <Textarea
                  data-testid="edit-q-fr"
                  value={editingQuestion.question_text_fr}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text_fr: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-[hsl(215,20%,65%)]">
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              data-testid="confirm-edit-btn"
              onClick={handleUpdateQuestion}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {i18n.language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {i18n.language === 'fr' ? 'Supprimer la question?' : 'Delete Question?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(215,20%,65%)]">
              {deletingQuestion?.question_text_en}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,17%)]">
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDeleteQuestion}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">
              {i18n.language === 'fr' ? 'Aperçu du sondage' : 'Survey Preview'}
            </DialogTitle>
            <DialogDescription>
              {hotel?.name} - {i18n.language === 'fr' ? 'Sondage Toilettes' : 'Restroom Survey'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {enabledQuestions.length === 0 ? (
              <div className="text-center py-4">
                <Droplets className="w-10 h-10 text-blue-400/50 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  {i18n.language === 'fr' 
                    ? 'Aucune question. Les clients verront seulement le champ de commentaires.'
                    : 'No questions. Guests will only see the comments field.'}
                </p>
              </div>
            ) : (
              enabledQuestions.sort((a, b) => a.order - b.order).map((q, i) => (
                <div key={q.id} className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    {i + 1}. {i18n.language === 'fr' ? q.question_text_fr : q.question_text_en}
                  </p>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-6 h-6 text-gray-300" />
                    ))}
                  </div>
                </div>
              ))
            )}
            
            {/* Notes field preview */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {i18n.language === 'fr' ? 'Commentaires (optionnel)' : 'Comments (optional)'}
              </p>
              <div className="h-16 bg-gray-100 rounded-lg"></div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)}>
              {i18n.language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
